import React from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import Card from 'components/card/Card';
import { MdAdd, MdDeleteOutline, MdLibraryAddCheck } from 'react-icons/md';
import { getSessionCandidateId, getSessionUserType } from 'helpers/session';

type CandidateOption = {
  id: number;
  Nombre?: string | null;
  ApellidoP?: string | null;
  ApellidoM?: string | null;
};

type QuestionItem = {
  id: number;
  Tipo: string;
  Pregunta: string;
  Respuesta: string;
};

type FormValues = {
  Titulo: string;
  FechaI: string;
  FechaF: string;
  R_Candidato_id?: string;
};

const QUESTION_TYPES = [
  { value: '1', label: 'Abierta' },
  { value: '2', label: 'Binaria' },
  { value: '3', label: 'Multiple' },
];

const emptyQuestion = (id: number): QuestionItem => ({
  id,
  Tipo: '1',
  Pregunta: '',
  Respuesta: '',
});

export default function NuevaEncuesta() {
  const navigate = useNavigate();
  const toast = useToast();
  const userType = getSessionUserType();
  const sessionCandidateId = getSessionCandidateId();
  const textColor = useColorModeValue('navy.700', 'white');
  const mutedText = useColorModeValue('secondaryGray.600', 'secondaryGray.400');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const panelBg = useColorModeValue('white', 'navy.800');
  const [questions, setQuestions] = React.useState<QuestionItem[]>([emptyQuestion(1)]);
  const [candidates, setCandidates] = React.useState<CandidateOption[]>([]);

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      Titulo: '',
      FechaI: '',
      FechaF: '',
      R_Candidato_id: sessionCandidateId ? String(sessionCandidateId) : '',
    },
  });

  React.useEffect(() => {
    if (sessionCandidateId) {
      setValue('R_Candidato_id', String(sessionCandidateId));
    }
  }, [sessionCandidateId, setValue]);

  React.useEffect(() => {
    if (userType === 'admin') {
      fetch(process.env.REACT_APP_URL_SERVER + '/candidatos', { credentials: 'include' })
        .then((res) => res.json())
        .then((result) => setCandidates(Array.isArray(result) ? result : []))
        .catch(() => setCandidates([]));
    }
  }, [userType]);

  const addQuestion = () => {
    setQuestions((current) => [...current, emptyQuestion(current.length ? current[current.length - 1].id + 1 : 1)]);
  };

  const updateQuestion = (id: number, field: keyof QuestionItem, value: string) => {
    setQuestions((current) => current.map((question) => (
      question.id === id ? { ...question, [field]: value } : question
    )));
  };

  const removeQuestion = (id: number) => {
    setQuestions((current) => current.length === 1 ? current : current.filter((question) => question.id !== id));
  };

  const onSubmit = async (values: FormValues) => {
    const candidateId = values.R_Candidato_id || (sessionCandidateId ? String(sessionCandidateId) : '');

    if (!candidateId) {
      toast({ title: 'Selecciona un candidato', status: 'warning', duration: 3000 });
      return;
    }

    if (questions.some((question) => !question.Pregunta.trim())) {
      toast({ title: 'Todas las preguntas deben tener texto', status: 'warning', duration: 3000 });
      return;
    }

    if (questions.some((question) => question.Tipo !== '1' && !question.Respuesta.trim())) {
      toast({ title: 'Las preguntas binarias o múltiples requieren opciones', status: 'warning', duration: 3000 });
      return;
    }

    const payload = questions.map((question) => ({
      Tipo: question.Tipo,
      Pregunta: question.Pregunta.trim(),
      Respuesta: question.Tipo === '1' ? '' : question.Respuesta.trim(),
    }));

    const formData = new FormData();
    formData.append('R_Candidato_id', candidateId);
    formData.append('Titulo', values.Titulo.trim());
    formData.append('FechaI', values.FechaI);
    formData.append('FechaF', values.FechaF);
    formData.append('Preguntas', JSON.stringify(payload));

    const response = await fetch(process.env.REACT_APP_URL_SERVER + '/new_encuesta', {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      toast({ title: 'No fue posible crear la encuesta', status: 'error', duration: 3500 });
      return;
    }

    toast({ title: 'Encuesta creada', status: 'success', duration: 2500 });
    navigate('/admin/main/ERP/Candidatos/RegistroEncuesta');
  };

  return (
    <Flex direction="column" minH="100vh" pt={{ sm: '125px', lg: '75px' }}>
      <Card p={{ base: '20px', md: '30px' }} bg={panelBg}>
        <Stack spacing={8}>
          <Box>
            <Heading color={textColor} fontSize={{ base: '28px', md: '34px' }} mb="8px">
              Nueva Encuesta
            </Heading>
            <Text color={mutedText}>
              Crea encuestas territoriales para levantar percepción, intención de voto o seguimiento operativo.
            </Text>
          </Box>

          <Box as="form" onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={8}>
              <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px">
                <FormControl isInvalid={!!errors.Titulo}>
                  <FormLabel color={textColor} fontWeight="700">Titulo</FormLabel>
                  <Input
                    placeholder="Encuesta territorial marzo"
                    borderRadius="16px"
                    h="50px"
                    {...register('Titulo', { required: 'El título es obligatorio.' })}
                  />
                  <FormErrorMessage>{errors.Titulo?.message}</FormErrorMessage>
                </FormControl>

                {userType === 'admin' ? (
                  <FormControl isInvalid={!!errors.R_Candidato_id}>
                    <FormLabel color={textColor} fontWeight="700">Candidato</FormLabel>
                    <Select
                      placeholder="Selecciona un candidato"
                      borderRadius="16px"
                      h="50px"
                      {...register('R_Candidato_id', { required: 'Selecciona un candidato.' })}
                    >
                      {candidates.map((candidate) => (
                        <option key={candidate.id} value={candidate.id}>
                          {[candidate.Nombre, candidate.ApellidoP, candidate.ApellidoM].filter(Boolean).join(' ')}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>{errors.R_Candidato_id?.message}</FormErrorMessage>
                  </FormControl>
                ) : (
                  <FormControl>
                    <FormLabel color={textColor} fontWeight="700">Candidato</FormLabel>
                    <Input value={sessionCandidateId ? `Asignado a candidato #${sessionCandidateId}` : 'No disponible'} isReadOnly borderRadius="16px" h="50px" />
                  </FormControl>
                )}

                <FormControl isInvalid={!!errors.FechaI}>
                  <FormLabel color={textColor} fontWeight="700">Fecha de inicio</FormLabel>
                  <Input type="date" borderRadius="16px" h="50px" {...register('FechaI', { required: 'La fecha inicial es obligatoria.' })} />
                  <FormErrorMessage>{errors.FechaI?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.FechaF}>
                  <FormLabel color={textColor} fontWeight="700">Fecha de cierre</FormLabel>
                  <Input type="date" borderRadius="16px" h="50px" {...register('FechaF', { required: 'La fecha final es obligatoria.' })} />
                  <FormErrorMessage>{errors.FechaF?.message}</FormErrorMessage>
                </FormControl>
              </SimpleGrid>

              <Stack spacing={4}>
                <Flex align="center" justify="space-between" wrap="wrap" gap="10px">
                  <Box>
                    <Text color={textColor} fontSize="lg" fontWeight="800">Preguntas</Text>
                    <Text color={mutedText} fontSize="sm">
                      Usa preguntas abiertas o define opciones separadas por coma para preguntas binarias o múltiples.
                    </Text>
                  </Box>
                  <Button leftIcon={<Icon as={MdAdd} />} variant="outline" borderRadius="14px" onClick={addQuestion}>
                    Agregar pregunta
                  </Button>
                </Flex>

                <Stack spacing={4}>
                  {questions.map((question, index) => (
                    <Box key={question.id} border="1px solid" borderColor={borderColor} borderRadius="22px" p={{ base: 4, md: 5 }}>
                      <Stack spacing={4}>
                        <HStack justify="space-between" align="center">
                          <Text color={textColor} fontWeight="800">Pregunta {index + 1}</Text>
                          <IconButton aria-label="Eliminar pregunta" icon={<Icon as={MdDeleteOutline} />} variant="ghost" colorScheme="red" onClick={() => removeQuestion(question.id)} isDisabled={questions.length === 1} />
                        </HStack>

                        <SimpleGrid columns={{ base: 1, md: 2 }} gap="16px">
                          <FormControl>
                            <FormLabel color={textColor} fontWeight="700">Tipo</FormLabel>
                            <Select borderRadius="16px" value={question.Tipo} onChange={(event) => updateQuestion(question.id, 'Tipo', event.target.value)}>
                              {QUESTION_TYPES.map((type) => (
                                <option key={type.value} value={type.value}>{type.label}</option>
                              ))}
                            </Select>
                          </FormControl>

                          <FormControl>
                            <FormLabel color={textColor} fontWeight="700">Pregunta</FormLabel>
                            <Input borderRadius="16px" placeholder="¿Cuál es su principal preocupación?" value={question.Pregunta} onChange={(event) => updateQuestion(question.id, 'Pregunta', event.target.value)} />
                          </FormControl>
                        </SimpleGrid>

                        {question.Tipo !== '1' ? (
                          <FormControl>
                            <FormLabel color={textColor} fontWeight="700">Opciones</FormLabel>
                            <Input borderRadius="16px" placeholder="Si, No o Morena, PAN, PRI" value={question.Respuesta} onChange={(event) => updateQuestion(question.id, 'Respuesta', event.target.value)} />
                            <Text mt="8px" fontSize="xs" color={mutedText}>Separa cada opción con coma.</Text>
                          </FormControl>
                        ) : null}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Stack>

              <Flex justify="flex-end" gap="12px" wrap="wrap">
                <Button variant="ghost" borderRadius="16px" onClick={() => navigate('/admin/main/ERP/Candidatos/RegistroEncuesta')}>
                  Cancelar
                </Button>
                <Button type="submit" variant="brand" borderRadius="16px" leftIcon={<Icon as={MdLibraryAddCheck} />} isLoading={isSubmitting}>
                  Guardar Encuesta
                </Button>
              </Flex>
            </Stack>
          </Box>
        </Stack>
      </Card>
    </Flex>
  );
}
